import type { ReactNode } from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { getAuthToken, logoutUser } from '@leanpay/services'
import { LoadingIndicator } from '@leanpay/ui'

import { useAppStore } from './app-store'
import {
    ACCESS_DENIED_ERROR_STATE,
    CreditRoute,
    MARKETING_ENTRY_PATH,
    UPSELL_FLOW,
} from 'library/constants'
import {
    useExchangeHandoff,
    useGplMarketingCheckoutInit,
    useUpsellCheckoutInit,
    useValidateCreditToken,
} from 'library/hooks/query-hooks'
import { navigateToHandoffLanding } from 'library/utils'

const CREDIT_ERROR_PATH = `/${CreditRoute.CreditError}`

type Entry =
    | { kind: 'handoff'; handoffCode: string; token: string | null; isUpsell: boolean }
    | { kind: 'upsell'; token: string }
    | { kind: 'classic'; token: string }
    | { kind: 'marketing' }
    | { kind: 'missingToken' }
    | { kind: 'none' }

// A handoff code wins over the token entries: the checkout app already created the GPL request,
// and a second checkout-init would cancel it.
const resolveEntry = (pathname: string, search: string): Entry => {
    if (pathname === MARKETING_ENTRY_PATH) return { kind: 'marketing' }
    if (pathname !== '/') return { kind: 'none' }

    const params = new URLSearchParams(search)
    const token = params.get('token')
    const handoffCode = params.get('handoffCode')
    const isUpsell = params.get('flow') === UPSELL_FLOW

    if (handoffCode) return { kind: 'handoff', handoffCode, token, isUpsell }
    if (!token) return { kind: 'missingToken' }
    if (isUpsell) return { kind: 'upsell', token }
    return { kind: 'classic', token }
}

const AppInit = ({ children }: { children: ReactNode }) => {
    const { setVendorToken, resetStore, isExiting, isAuthenticated, vendorToken } = useAppStore()
    const navigate = useNavigate()
    const { pathname, search } = useLocation()
    const entry = useMemo(() => resolveEntry(pathname, search), [pathname, search])

    const tokenValidation = useValidateCreditToken(entry.kind === 'classic' ? entry.token : null)
    const { mutate: initUpsellCheckout, isIdle: isUpsellIdle } = useUpsellCheckoutInit()
    const { mutate: initMarketingCheckout, isIdle: isMarketingIdle } = useGplMarketingCheckoutInit()
    const { mutate: exchangeHandoff } = useExchangeHandoff()

    const hasAccessError =
        entry.kind === 'missingToken' || (entry.kind === 'classic' && tokenValidation.isError)

    useEffect(() => {
        if (entry.kind !== 'classic' || !tokenValidation.isSuccess) return
        resetStore()
        logoutUser()
        setVendorToken(entry.token)
        navigate(`/${CreditRoute.RequestOtp}`, { replace: true })
    }, [entry, tokenValidation.isSuccess, navigate, resetStore, setVendorToken])

    // Each init below runs once per page load. StrictMode runs effects twice in dev, and a second
    // checkout-init cancels the first GPL request; a second handoff exchange gets a 409.
    const upsellCheckoutFiredRef = useRef(false)
    useEffect(() => {
        if (upsellCheckoutFiredRef.current) return
        if (entry.kind !== 'upsell' || !isUpsellIdle) return
        upsellCheckoutFiredRef.current = true
        initUpsellCheckout(entry.token)
    }, [entry, isUpsellIdle, initUpsellCheckout])

    const marketingCheckoutFiredRef = useRef(false)
    useEffect(() => {
        if (marketingCheckoutFiredRef.current) return
        if (entry.kind !== 'marketing' || !isMarketingIdle) return
        marketingCheckoutFiredRef.current = true
        initMarketingCheckout()
    }, [entry, isMarketingIdle, initMarketingCheckout])

    const handoffExchangedRef = useRef(false)
    useEffect(() => {
        if (handoffExchangedRef.current) return
        if (entry.kind !== 'handoff') return
        handoffExchangedRef.current = true

        // A refresh sends the spent code again. Keep the session only if its idToken is still in
        // localStorage: the sessionStorage flags can outlive a logout in another tab.
        const hasSessionForThisToken =
            isAuthenticated && vendorToken === entry.token && !!getAuthToken()
        if (hasSessionForThisToken) {
            navigateToHandoffLanding(navigate)
            return
        }

        // The exchange endpoint is anonymous, so the old token must go before the call.
        resetStore()
        logoutUser()
        exchangeHandoff({
            handoffCode: entry.handoffCode,
            fallbackVendorToken: entry.token,
            isUpsell: entry.isUpsell,
        })
    }, [entry, isAuthenticated, vendorToken, exchangeHandoff, navigate, resetStore])

    if (isExiting) return <LoadingIndicator />

    if (hasAccessError) {
        return <Navigate to={CREDIT_ERROR_PATH} state={ACCESS_DENIED_ERROR_STATE} replace />
    }

    // Every entry navigates away from itself; until then only the loader shows.
    if (entry.kind !== 'none') return <LoadingIndicator />

    return children
}

export default AppInit
